// @flow
import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import {Typography, Tabs, Tab, AppBar, Snackbar} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import StarRating from './StarRating/StarRating';
import PendingReviewsList from './PendingReviewsList/PendingReviewsList';
import ReviewsList from './ReviewsList/ReviewsList';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useIsFeatureInTier} from '../../../../utils/component';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';

import type {
  Pagination as PaginationType,
  Order,
  Store,
  Review,
  User,
  Currency,
} from '../../../../entities';
import {usePage} from '../../../page/utils';
import {useCallback} from 'react';
import {Alert} from '@material-ui/lab';

type Props = {
  enableReview: ({enabled: boolean, orderId: string}) => void,
  fetchPendingReviews: ({queryFilter?: {[key: string]: string}}) => void,
  isFetchingPendingReviews?: boolean,
  isFetchingReviews?: boolean,
  fetchReviews: ({queryFilter?: {[key: string]: string}}) => void,
  paginationReviewsPending?: PaginationType,
  paginationReviews?: PaginationType,
  pendingReviews?: Order[],
  store?: Store,
  reviews?: Review[],
  fetchAverage: () => void,
  average?: string,
  isFetchingAverage?: boolean,
  user?: User,
  currency?: Currency,
  enableReviewSuccess?: boolean,
  disableReviewSuccess?: boolean,
};

const useStyles = makeStyles(theme => ({
  tabReviews: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    margin: 'auto',
    transform: 'none',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colors.grey300}`,
  },
  blur: {
    filter: 'blur(2px)',
  },
  starRating: {
    margin: '8px 16px',
  },
  wrapReviews: {
    background: '#F8F9FB',
    boxShadow: '0px',
    borderRadius: '0px',
    [theme.breakpoints.up('md')]: {
      background: 'white',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  tierRestrictionModalWrapper: {
    position: 'absolute',
    top: 200,
    // top: '50%',
    // transform: 'translateY(-50%)',
    // zIndex: 1103,
    margin: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      top: 138,
      // top: 'calc(50% - 36px)',
      left: 0,
      margin: theme.spacing(2),
    },
    maxHeight: 'calc(100vh - 64px)',
    zIndex: 3,
    alignSelf: 'center',
    justifySelf: 'center',
  },
  mejorarPlanButton: {
    marginLeft: 4,
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
}));

export default usePage<Props>(Reviews, 'Reviews');

function Reviews(props: Props) {
  const {
    enableReview,
    paginationReviewsPending,
    paginationReviews,
    pendingReviews,
    fetchReviews,
    fetchPendingReviews,
    isFetchingPendingReviews,
    isFetchingReviews,
    reviews,
    store,
    fetchAverage,
    average,
    isFetchingAverage,
    user,
    currency,
    enableReviewSuccess,
    disableReviewSuccess,
  } = props;
  const classes = useStyles();

  const [valueTab, setValueTab] = useState(0);

  const [tierRestrictedModal, setTierRestrictedModal] = useState(false);
  const [tierRestricted, setTierRestricted] = useState(false);
  const [reviewAdvice, setReviewAdvice] = useState(false);

  const hasDashboardFeature = useIsFeatureInTier('ORDER_MANAGEMENT_DASHBOARD');

  useEffect(() => {
    if (!hasDashboardFeature) {
      setTierRestricted(true);
    }
  }, [hasDashboardFeature, store]);

  const tierRestrictionModal = (
    <div className={classes.tierRestrictionModalWrapper}>
      <TierRestrictionModal
        open={true}
        handleClose={() => setTierRestrictedModal(false)}
      />
    </div>
  );

  const handleChangeTab = (event, newValue) => {
    setValueTab(newValue);
  };

  const featuredReviews =
    reviews && reviews.length ? reviews.filter(r => r.featured) : [];

  const enableSingleReview = useCallback(
    (req: {enabled: boolean, orderId: string, featured?: boolean}) => {
      if (valueTab === 1) {
        if (req.featured && featuredReviews.length < 2) setReviewAdvice(true);
      }
      enableReview(req);
    },
    [enableReview, featuredReviews.length, valueTab]
  );

  useEffect(() => {
    if (user) fetchAverage();
  }, [fetchAverage, user]);

  if (!user || !store || isFetchingAverage) return null;
  return (
    <Fragment>
      <Snackbar
        open={reviewAdvice}
        autoHideDuration={4000}
        onClose={() => setReviewAdvice(false)}
      >
        <Alert severity="info" onClose={() => setReviewAdvice(false)}>
          Para mostrar las reseñas en tu tienda debes tener al menos 3
          destacadas.
        </Alert>
      </Snackbar>
      {tierRestrictedModal ? tierRestrictionModal : null}
      {tierRestricted && (
        <div
          style={{
            margin: '16px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <CustomButton
            className={classes.mejorarPlanButton}
            variant="contained"
            color="default"
            onClick={() => setTierRestrictedModal(true)}
          >
            Mejorar a TiendaDa PRO
          </CustomButton>
        </div>
      )}

      <div
        className={`${classes.wrapReviews} ${
          tierRestricted && tierRestrictedModal ? classes.blur : ''
        }`}
      >
        <AppBar position="static" className={classes.tabReviews}>
          <Tabs
            value={valueTab}
            onChange={handleChangeTab}
            indicatorColor="primary"
            style={{justifyContent: 'space-between'}}
          >
            <Tab
              style={{justifyContent: 'space-between'}}
              label={
                <Typography
                  align="center"
                  variant="body1"
                  style={{
                    fontSize: '12px',
                    color: `${colors.grey900}`,
                  }}
                >
                  Pendientes
                </Typography>
              }
            />
            <Tab
              style={{justifyContent: 'space-between'}}
              label={
                <Typography
                  align="center"
                  variant="body1"
                  style={{
                    fontSize: '12px',
                    color: `${colors.grey900}`,
                  }}
                >
                  Publicadas
                </Typography>
              }
            />
          </Tabs>
          {valueTab === 1 && (
            <StarRating
              className={classes.starRating}
              punctuation={Number(average)}
            />
          )}
        </AppBar>
        {valueTab === 0 ? (
          <PendingReviewsList
            fetchReviews={fetchPendingReviews}
            isFetchingReviews={isFetchingPendingReviews}
            reviews={pendingReviews}
            pagination={paginationReviewsPending}
            store={store}
            currency={currency}
            tierRestricted={tierRestricted}
          />
        ) : (
          <ReviewsList
            enableReview={enableSingleReview}
            fetchReviews={fetchReviews}
            isFetchingReviews={isFetchingReviews}
            reviews={reviews}
            pagination={paginationReviews}
            enableReviewSuccess={enableReviewSuccess}
            disableReviewSuccess={disableReviewSuccess}
          />
        )}
      </div>
    </Fragment>
  );
}

Reviews.propTypes = {
  enableReview: PropTypes.func.isRequired,
  fetchPendingReviews: PropTypes.func.isRequired,
  isFetchingPendingReviews: PropTypes.bool,
  isFetchingReviews: PropTypes.bool,
  fetchReviews: PropTypes.func.isRequired,
  paginationReviewsPending: PropTypes.object,
  paginationReviews: PropTypes.object,
  pendingReviews: PropTypes.array,
  reviews: PropTypes.array,
  store: PropTypes.object,
  fetchAverage: PropTypes.func.isRequired,
  average: PropTypes.string,
  isFetchingAverage: PropTypes.bool,
  user: PropTypes.object,
  currency: PropTypes.object,
  enableReviewSuccess: PropTypes.bool,
  disableReviewSuccess: PropTypes.bool,
};
